import { Merchant, MerchantType } from "src/interfaces/interfaces";
import { PermissionRole } from "src/constants";

/**
 * snake_case → camelCase mappers lifted out of the Redux reducers so the
 * translation survives the slices being deleted.
 *
 * These deliberately keep values presentation-free: dates stay as raw ISO
 * strings and are formatted in the view. The old `merchantUsersSlice` formatted
 * dates inside the reducer, which baked a display format into cached data.
 */

/** GET merchant/linked — the location list. */
export interface LinkedLocation {
  id: number;
  name: string;
  logoUrl: string;
  typeId: number;
  userPermissionRole: PermissionRole;
}

export const mapLinkedLocation = (raw: any): LinkedLocation => ({
  id: raw.id,
  name: raw.name,
  logoUrl: raw.logo_url,
  typeId: raw.type_id,
  userPermissionRole: raw.permission_role,
});

const mapMerchantType = (raw: any): MerchantType => ({
  id: raw.id,
  name: raw.name,
  display_name: raw.display_name,
  description: raw.description,
});

const mapAddress = (raw: any) =>
  raw
    ? {
        ...raw,
        placeId: raw.place_id,
        countryCode: raw.country_code,
        lat: raw.lat,
        lng: raw.lng,
      }
    : null;

/**
 * GET merchant/id — the full location record.
 *
 * Note `payments` hangs off the TOP-LEVEL payload, not off `subscription`, even
 * though it is nested under `subscription` in the shape we expose.
 */
export const mapMerchant = (raw: any): Merchant => {
  if (!raw || typeof raw.id === "undefined") {
    throw new Error("Received invalid merchant data from server");
  }

  return {
    id: raw.id,
    name: raw.name,
    location: raw.location,
    logoUrl: raw.logo_url,
    // `type` is an object only because the request sends `x-app-version`.
    // Without that header the API returns a bare string here.
    type: mapMerchantType(raw.type),
    totalTarget: raw.total_target,
    streakTarget: raw.streak_target,
    streaksEnabled: raw.streaks_enabled,
    freeFlyersRemaining: raw.free_flyers_remaining,
    address: mapAddress(raw.address),
    operatingHours: raw.operating_hours
      ? raw.operating_hours.map((hours: any) => ({
          dayOfWeek: hours.day_of_week,
          openTime: hours.open_time,
          closeTime: hours.close_time,
        }))
      : null,
    subscription: raw.subscription
      ? {
          id: raw.subscription.id,
          token: raw.subscription.subscription_token,
          status: raw.subscription.status,
          billingDate: raw.subscription.billing_date,
          payments: (raw.payments ?? []).map((payment: any) => ({
            id: payment.id,
            amount: payment.amount_gross,
            pfPaymentId: payment.pf_payment_id,
            date: payment.date_created,
            invoiceUrl: payment.online_invoice_url,
          })),
        }
      : null,
    adHocClosingDays: raw.ad_hoc_closing_days
      ? raw.ad_hoc_closing_days.map((day: any) => ({ id: day.id, date: day.date }))
      : null,
    billingEmail: raw.billing_email,
    billingBusinessName: raw.billing_business_name,
    billingContactName: raw.billing_contact_name,
    billingBusinessRegistrationNumber: raw.billing_business_registration_number,
    billingTaxNumber: raw.billing_tax_number,
    referralCode: raw.referral_code,
  };
};

/** GET merchant/:id/users */
export interface MerchantUser {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  /** Raw ISO string — format in the view, not here. */
  dateCreated: string;
  permissionRole: PermissionRole;
}

export const mapMerchantUser = (raw: any): MerchantUser => ({
  id: raw.id,
  firstName: raw.first_name,
  lastName: raw.last_name,
  email: raw.email,
  dateCreated: raw.date_created,
  permissionRole: raw.permission_role,
});

/** GET loyalty/stats */
export interface Streak {
  currentStreak: number;
  currentTotal: number;
  lastPurchaseDate?: string;
}

export interface Purchase {
  date: string;
  cumulativeCount: number;
  userId: number;
  firstName: string;
  lastName: string;
}

export interface Reward {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  redeemed: boolean;
  triggerType: string;
  dateCreated: string;
  dateRedeemed: string;
}

export interface LoyaltyStats {
  streaks: Streak[];
  purchases: Purchase[];
  rewards: Reward[];
}

export const mapLoyaltyStats = (raw: any): LoyaltyStats => ({
  streaks: (raw?.streaks ?? []).map((streak: any) => ({
    currentStreak: streak.current_streak,
    currentTotal: streak.current_total,
    lastPurchaseDate: streak.last_purchase_date,
  })),
  purchases: (raw?.purchases ?? []).map((purchase: any) => ({
    date: purchase.date_created,
    cumulativeCount: purchase.cumulative_count,
    userId: purchase.user_id,
    firstName: purchase.first_name,
    lastName: purchase.last_name,
  })),
  rewards: (raw?.rewards ?? []).map((reward: any) => ({
    id: reward.id,
    userId: reward.user_id,
    firstName: reward.user?.first_name,
    lastName: reward.user?.last_name,
    redeemed: reward.redeemed,
    triggerType: reward.trigger_type,
    dateCreated: reward.date_created,
    dateRedeemed: reward.date_updated,
  })),
});
